// © Thibault Sabathier, 2025. Tous droits réservés.
// frontend/src/components/AdminProtectedRoute.jsx

import React, { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { CSpinner, CAlert } from '@coreui/react'
import api from '../utils/apiClient'

/**
 * Composant de protection des routes super-admin
 * Vérifie la présence et la validité du token JWT super-admin
 */
const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const location = useLocation()

  // Security: adminUserId in localStorage is for UI state only - auth handled via httpOnly cookies
  const adminUserId = localStorage.getItem('adminUserId') || localStorage.getItem('userId')

  useEffect(() => {
    const verifyAdminAuth = async () => {
      if (!adminUserId) {
        setIsAuthenticated(false)
        setLoading(false)
        return
      }

      try {
        // Verify auth by calling the endpoint - auth handled via httpOnly cookies
        const response = await api.get('/admin/me')

        // Si la requête réussit, l'utilisateur est authentifié et super-admin
        if (response.success && response.user.is_super_admin) {
          setIsAuthenticated(true)
        } else {
          setIsAuthenticated(false)
          setError('Vous devez être super-admin pour accéder à cette page')
        }
      } catch (err) {
        console.error('Erreur vérification admin:', err)

        if (err.response?.status === 401) {
          // Session invalide ou expirée
          localStorage.removeItem('adminUserId')
          setIsAuthenticated(false)
          setError('Session expirée ou invalide')
        } else if (err.response?.status === 403) {
          // Pas super-admin
          setIsAuthenticated(false)
          setError('Accès refusé - Droits super-admin requis')
        } else {
          // Autre erreur (serveur down, etc.)
          setIsAuthenticated(false)
          setError('Erreur de connexion au serveur')
        }
      } finally {
        setLoading(false)
      }
    }

    verifyAdminAuth()
  }, [adminUserId])

  // Afficher le spinner pendant la vérification
  if (loading) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <CSpinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3">Vérification des droits admin...</p>
        </div>
      </div>
    )
  }

  // Redirection vers /admin/login si pas authentifié (Phase 15)
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />
  }

  // Si authentifié, afficher le contenu
  return <>{children}</>
}

export default AdminProtectedRoute
